import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  Alert,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedTag } from "./ThemedTag";
import { TagSelector } from "./TagSelector";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useFirebase } from "@components/firebase/useFirebase";
import { ITagAssociationMeta, ITag, TagTargetType } from "common247";
import { removeTagFromEntity, getTags } from "@/services/firebase/tag-functions";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

export interface ITagListStripProps {
  entityId?: string;
  entityType: TagTargetType;
  tags?: Record<string, ITagAssociationMeta>;
  tagItems?: ITag[];
  canEdit?: boolean;
  isOwner?: boolean;
  currentUserId?: string;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
  onTagAdded?: (association: ITagAssociationMeta) => void;
  onTagRemoved?: (tagId: string) => void;
  onTagsChange?: (tags: Record<string, ITagAssociationMeta>) => void;
}

const EMPTY_TAG_MAP: Record<string, ITagAssociationMeta> = Object.freeze({});

/**
 * Standardized inline tag strip for item cards (Memory, Media, Contact, Group).
 * Renders associated tags with permissions-aware removal and "+ Tag" action button.
 */
export function TagListStrip({
  entityId = "",
  entityType,
  tags,
  tagItems,
  canEdit = false,
  isOwner = false,
  currentUserId,
  compact = false,
  style,
  onTagAdded,
  onTagRemoved,
  onTagsChange,
}: ITagListStripProps) {
  const services = useFirebase();
  const [modalOpen, setModalOpen] = useState(false);
  const [tagAnchor, setTagAnchor] = useState<IPopoverAnchor | null>(null);

  // Canonical tag normalization: strictly handles Record<string, ITagAssociationMeta> or null/undefined
  const normalizedInitialTags = useMemo(() => {
    if (!tags || typeof tags !== "object" || Array.isArray(tags)) return EMPTY_TAG_MAP;
    if (Object.keys(tags).length === 0) return EMPTY_TAG_MAP;
    return tags as Record<string, ITagAssociationMeta>;
  }, [tags]);

  const [localTags, setLocalTags] = useState<Record<string, ITagAssociationMeta>>(normalizedInitialTags);
  const [tagMetadataMap, setTagMetadataMap] = useState<Record<string, ITag>>({});
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Index tagItems by ID when provided
  useEffect(() => {
    if (tagItems && tagItems.length > 0) {
      setTagMetadataMap((prev) => {
        const next = { ...prev };
        tagItems.forEach((item) => {
          if (item?.id) next[item.id] = item;
        });
        return next;
      });
    }
  }, [tagItems]);

  // Fetch missing tag metadata for any tagId not in tagMetadataMap
  useEffect(() => {
    if (!services) return;
    const missingIds = Object.keys(localTags || {}).filter(
      (id) => !tagMetadataMap[id] && !((localTags[id] as any)?.value)
    );
    if (missingIds.length === 0) return;

    let isMounted = true;
    getTags(services, { tagIds: missingIds })
      .then((res) => {
        if (!isMounted || !res.status || !res.tags) return;
        setTagMetadataMap((prev) => {
          const next = { ...prev };
          res.tags.forEach((t) => {
            if (t?.id) next[t.id] = t;
          });
          return next;
        });
      })
      .catch((err) => {
        console.warn("[TagListStrip] Failed to fetch tag details for tagIds:", missingIds, err);
      });

    return () => {
      isMounted = false;
    };
  }, [localTags, tagMetadataMap, services]);

  // Only sync when tag keys or counts actually change to prevent recursive setState loops
  useEffect(() => {
    setLocalTags((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(normalizedInitialTags || {});
      if (
        prevKeys.length === nextKeys.length &&
        prevKeys.every((k) => k in normalizedInitialTags)
      ) {
        return prev;
      }
      return normalizedInitialTags;
    });
  }, [normalizedInitialTags]);

  const tagList = Object.values(localTags || {});
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");

  const handleRemoveTag = async (tag: ITagAssociationMeta) => {
    if (!services || removingId) return;

    const performRemoval = async () => {
      setRemovingId(tag.tagId);
      try {
        if (entityId) {
          const res = await removeTagFromEntity(services, {
            targetType: entityType,
            targetId: entityId,
            tagId: tag.tagId,
          });

          if (!res.status) {
            const msg = res.error || "Failed to remove tag.";
            if (Platform.OS === "web") {
              window.alert(msg);
            } else {
              Alert.alert("Error", msg);
            }
            return;
          }
        }

        const updated = { ...localTags };
        delete updated[tag.tagId];
        setLocalTags(updated);
        onTagRemoved?.(tag.tagId);
        onTagsChange?.(updated);
      } catch (err: any) {
        console.error("[TagListStrip] Error removing tag:", err);
        const msg = err.message || "Failed to remove tag.";
        if (Platform.OS === "web") {
          window.alert(msg);
        } else {
          Alert.alert("Error", msg);
        }
      } finally {
        setRemovingId(null);
      }
    };

    const resolvedDisplay = tagMetadataMap[tag.tagId]?.value || (tag as any).value || tag.tagId;
    if (Platform.OS === "web") {
      await performRemoval();
    } else {
      Alert.alert(
        "Remove Tag",
        `Remove tag "${resolvedDisplay}" from this ${entityType}?`,
        [
          { text: "Cancel", style: "cancel" },
          { text: "Remove", style: "destructive", onPress: performRemoval },
        ]
      );
    }
  };

  const handleTagAdded = (association: ITagAssociationMeta) => {
    const updated = {
      ...(localTags || {}),
      [association.tagId]: association,
    };
    setLocalTags(updated);
    onTagAdded?.(association);
    onTagsChange?.(updated);
  };

  // If no tags and user cannot edit, don't render an empty container
  if (tagList.length === 0 && !canEdit) {
    return null;
  }

  return (
    <View
      style={[styles.container, compact && styles.containerCompact, style]}
      {...(Platform.OS === "web"
        ? {
            onClick: (e: any) => e?.stopPropagation?.(),
          }
        : {})}
    >
      <View style={styles.tagFlexRow}>
        {tagList.map((t) => {
          // Removal rule: during creation (!entityId), or entity owner OR user who added the tag
          const canRemove = Boolean(
            !entityId || isOwner || (currentUserId && t.addedBy === currentUserId)
          );

          const tagMeta = tagMetadataMap[t.tagId];
          const resolvedTag = tagMeta
            ? { ...t, value: tagMeta.value, tagType: tagMeta.tagType }
            : t;

          return (
            <ThemedTag
              key={t.tagId}
              tag={resolvedTag}
              compact={compact}
              canRemove={canRemove}
              onRemove={() => handleRemoveTag(t)}
            />
          );
        })}

        {canEdit && (
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={(e: any) => {
              const x = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX ?? 0;
              const y = e?.nativeEvent?.pageY ?? e?.nativeEvent?.clientY ?? 0;
              setTagAnchor(x || y ? { x, y } : null);
              e?.stopPropagation?.();
              setModalOpen(true);
            }}
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
            style={[
              styles.addTagButton,
              compact && styles.addTagButtonCompact,
              { backgroundColor: badgeBg, borderColor },
            ]}
          >
            <Ionicons name="add" size={compact ? 10 : 11} color={tintColor} />
            <ThemedText style={[styles.addTagText, compact && styles.addTagTextCompact, { color: tintColor }]}>
              Tag
            </ThemedText>
          </TouchableOpacity>
        )}
      </View>

      {modalOpen && (
        <TagSelector
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          entityId={entityId}
          entityType={entityType}
          currentTags={localTags}
          anchorPosition={tagAnchor}
          onTagAdded={handleTagAdded}
        />
      )}
    </View>
  );
}

export default TagListStrip;

const styles = StyleSheet.create({
  container: {
    marginVertical: 1,
    width: "100%",
  },
  containerCompact: {
    marginVertical: 0,
  },
  tagFlexRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  addTagButton: {
    height: 19,
    maxHeight: 19,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 5,
    paddingVertical: 0,
    borderRadius: 4,
    borderWidth: 1,
    marginVertical: 1,
    marginRight: 6,
    gap: 2.5,
  },
  addTagButtonCompact: {
    height: 17,
    maxHeight: 17,
    paddingHorizontal: 4,
    paddingVertical: 0,
    borderRadius: 3,
    marginVertical: 0,
    marginRight: 4,
    gap: 2,
  },
  addTagText: {
    fontSize: 11,
    lineHeight: 15,
    fontWeight: "600",
  },
  addTagTextCompact: {
    fontSize: 10,
    lineHeight: 13,
  },
});

